import React from 'react'
import { countryDisplayName } from '../../utils/format'
import { countryFlagUrl } from '../../utils/countryFlags'

interface CountryFlagIconProps {
  code: string
  className?: string
  testId?: string
  /**
   * Make the flag a tab stop so keyboard users can reach the tooltip.
   * Turn off when the flag sits inside a link or button.
   */
  focusable?: boolean
  /** sm: 18×12 inline with text. lg: 27×18 next to a page title. */
  size?: 'sm' | 'lg'
}

const SIZES = {
  sm: { w: 18, h: 12, img: 'h-3 w-[18px]' },
  lg: { w: 27, h: 18, img: 'h-[18px] w-[27px]' },
} as const

/**
 * Country indicator: an SVG flag with the Danish country name in a
 * tooltip on hover and focus (DESIGN.md → Lande). Never a country code.
 */
const CountryFlagIcon: React.FC<CountryFlagIconProps> = ({
  code,
  className = '',
  testId = 'country-flag-icon',
  focusable = true,
  size = 'sm',
}) => {
  const dims = SIZES[size]
  const name = countryDisplayName(code)
  const url = countryFlagUrl(code)
  if (!url) return null
  return (
    <span
      className={`group/flag relative inline-flex shrink-0 cursor-default align-[-1px] rounded-[2px] ${className}`}
      role="img"
      aria-label={name}
      tabIndex={focusable ? 0 : undefined}
      data-testid={testId}
    >
      <img
        src={url}
        alt=""
        width={dims.w}
        height={dims.h}
        className={`block ${dims.img} rounded-[2px] outline outline-1 -outline-offset-1 outline-ink/20`}
        draggable={false}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-[5px] bg-ink px-2 py-1 text-xs font-semibold leading-none text-paper opacity-0 transition-opacity duration-100 group-hover/flag:opacity-100 group-focus-visible/flag:opacity-100"
      >
        {name}
      </span>
    </span>
  )
}

export default CountryFlagIcon
